import { Layout, SEO } from '@components/common'
import { Container } from '@components/ui'
import Link from 'next/link'

export default function Guarantee() {
  return (
    <>
      <SEO
        title="Yadav Shipping And Guarantees | Yadav Diamonds and Jewelry"
        description="Experience worry-free shopping - Yadav Shipping and Guarantees ensure secure delivery and customer satisfaction. Explore with confidence"
      />
      <Container className="my-8 lg:my-16">
        <h1 className="font-heading font-normal">
          Yadav Shipping and Guarantees
        </h1>
        <nav className="my-8 lg:my-16">
          <ul>
            <li className="mb-2">
              <a
                href="#shipping-policy"
                className="hover:underline"
              >
                Shipping Policy
              </a>
            </li>
            <li className="mb-2">
              <a href="#money-back-guarantee" className="hover:underline">
                30 Day Money Back Guarantee
              </a>
            </li>
            <li className="mb-2">
              <a href="#lifetime-upgrades" className="hover:underline">
                Unlimited Lifetime Upgrades
              </a>
            </li>
          </ul>
        </nav>

        <section className="mb-6">
          <h2
            id="shipping-policy"
            className="font-heading font-normal text-xl mb-2"
          >
            Shipping Policy
          </h2>
          <ul className="list-disc pl-6">
            <li>
              Free Domestic Shipping
              <p className="mb-2">
                We offer <strong className="font-bold">free shipping anywhere in the U.S.</strong>, 
                including all 50 states. Every order is fully insured for peace of mind. 
                When your package arrives, we recommend inspecting it for any signs of damage 
                or tampering before signing for delivery.
              </p>
            </li>
            <li>
              Beautiful & Secure Packaging
              <p className="mb-2">
                Each purchase is carefully packaged in our signature Yadav box for a beautiful unboxing experience. 
                Your order will also include the appropriate certification.
              </p>
            </li>
            <li>
              Return Shipping
              <p className="mb-2">
                We offer <strong className="font-bold">free return shipping within the U.S.</strong> (up 
                to <strong className="font-bold">2 returns per customer per year</strong>). For international returns, 
                customers are responsible for return shipping costs.
              </p>
            </li>
            <li>
              Domestic Shipping Details
              <p className="mb-2">
                We ship all U.S. orders via <strong className="font-bold">FedEx</strong> with no minimum purchase 
                required. <strong className="font-bold">Saturday delivery</strong> may be available depending on your location.
              </p>
            </li>
            <li>
              International Shipping
              <p className="mb-2">
                We use <strong className="font-bold">FedEx Worldwide Express®</strong> for reliable delivery 
                to over 200 countries. Most packages arrive within <strong className="font-bold">2 business days</strong>, 
                often by <strong className="font-bold">10:30 a.m.</strong> in major business centers.
              </p>
              <p className="mb-2">
                International orders have a <strong className="font-bold">$50 shipping fee</strong> and may be subject to import duties.
              </p>
            </li>
            <li>
              APO/FPO Addresses
              <p className="mb-2">
                We ship to military addresses using <strong className="font-bold">USPS Priority Mail®</strong>. 
                For these orders, we require <strong className="font-bold">payment via wire transfer</strong> and <strong className="font-bold">additional 
                  processing time</strong>.
              </p>
            </li>
            <li>
              Shipping to an Alternate Address
              <p className="mb-2">
                Want to keep your order a surprise? No problem. We can ship to an alternate address 
                to help keep gifts like engagement rings under wraps. 
                For security reasons, additional verification may be required. 
                Call us at
                <a
                  href="tel:1-888-968-8810"
                  className="text-accent-9"
                  title="Telephone"
                >
                  {' '}1-888-968-8810{' '}
                </a>to arrange this.
              </p>
            </li>
            <li>
              Production & Delivery Timeline
              <p className="mb-2">
                Most orders are shipped within <strong className="font-bold">4–5 weeks</strong>, 
                based on the production time of your jewelry. We do our best to meet your timeline, 
                especially for custom pieces. If you need it sooner than 4–5 weeks, please contact us 
                and we’ll do our best to accommodate you.
              </p>
            </li>
          </ul>
        </section>
        <section className="mb-6">
          <h2
            id="money-back-guarantee"
            className="font-heading font-normal text-xl mb-2"
          >
            30 Day Money Back Guarantee
          </h2>
          <p className="mb-2">
            To ensure all Yadav customers have great experience, purchases are
            covered under the Yadav 30-day money back guarantee.
          </p>
          <p className="mb-2">
            In order to process a return, please follow our Return Policy
          </p>
          <p className="mb-2">
            Yadav guarantees that all diamonds and jewelry will be durable and
            beautiful, and all of our products are free of any manufacturing
            defects at the time of delivery.
          </p>
          <ol className="pl-6 list-decimal">
            <li>
              <p>
                If the product is deemed to have a manufacturing defect, we will
                repair the item free of charge.
              </p>
            </li>
            <li>
              <p>
                If an item needs repair, you may send it back to our office for
                inspection.
              </p>
            </li>
            <li>
              <p>
                If the damage is caused by normal wear and tear, we will repair
                it at cost.
              </p>
            </li>
          </ol>
          <p className="mb-4">
            If you would like further clarification about our refund policy,
            please call Yadav customer service at{' '}
            <a
              href="tel:1-888-968-8810"
              className="text-accent-9"
              title="Telephone"
            >
              {' '}
              1-888-968-8810{' '}
            </a>{' '}
            between 9:30 am to 6:00 pm Pacific Time.
          </p>
        </section>
        <section className="mb-6">
          <h2
            id="lifetime-upgrades"
            className="font-heading font-normal text-xl mb-2"
          >
            Unlimited Lifetime Upgrades
          </h2>
          <p className="mb-2">
            After your 30-Day money back guarantee has expired, you will have the opportunity to upgrade your diamond(s).  The price of your new diamond must be at least twice the price of your original diamond.
          </p>
          <p className="mb-2">
            To upgrade, simply return the diamond(s) along with the original GIA certificates, and you will receive credit towards a new diamond purchase for the original price paid. Note: Upgrade credit does not include duty or tax paid on the original purchase
          </p>
          <p className="text-sm mt-6 my-2 font-bold">Natural Diamond Upgrade Program</p>
          <ul className="list-disc pl-6 mb-2">
            <li>
              <p>
                Upgrades are for the center natural diamond only.
              </p>
            </li>
            <li>
              <p>
                Colored gemstones, settings, and finished jewelry do not qualify for upgrading.
              </p>
            </li>
            <li>
              <p>The original diamond value may not exceed $35,000.</p>
            </li>
            <li>
              <p>
                A 20% restocking fee of the original diamond value will be applied.
              </p>
            </li>
            <li>
              <p>
                You will receive a credit equal to 100% of the original price of the diamond toward
                the purchase of a new diamond.

              </p>
            </li>
            <li>
              <p>
                The price of your new diamond must be at least twice the price of your original
                diamond.
              </p>
            </li>
            <li>
              <p>
                Your diamond must be in its original condition with the original GIA certificate
                report (Create-your-own diamond earrings without GIA or AGS Certificates are
                not eligible for upgrade).

              </p>
            </li>
            <li>
              <p>
                The original GIA certificate must be returned in reusable condition. Fees may apply
                for any damaged or unusable GIA certificate.
              </p>
            </li>
            <li>
              <p>
                Any damage to the original diamond may disqualify the diamond from our upgrade
                program. In rare instances, a diamond may have incurred damage through daily
                wear. In this case Yadav will still accept the diamond, but the cost of polishing and
                reshaping the diamond will be deducted from the original price.
              </p>
            </li>
            <li>
              <p>
                If your diamond is set in a Yadav setting and is in reusable condition, we can use it
                to set your new diamond for free. If the setting is not in reusable condition, an
                additional fee will apply for the modification.
              </p>
            </li>
            <li>
              <p>
                An additional fee will apply to set the setting with the new diamond if it wasn’t
                originally set up with Yadav. The fee will be determined on a case-by-case basis.
              </p>
            </li>
          </ul>
          <p className="text-sm mt-6 my-2 font-bold">Lab Grown Diamond Upgrade Program</p>
          <p className="mb-2">
            YADAV Diamond and Jewelry is excited to launch this new program for lab-grown
            diamonds. We are committed to offering only the highest-quality diamonds, which
            enables us to provide this exceptional upgrade program. This program applies to all
            lab-grown diamonds purchased after June 2024.
          </p>
          <ul className="list-disc pl-6 mb-2">
            <li>
              <p>
                Upgrades are for the center diamond only.
              </p>
            </li>
            <li>
              <p>
                Colored gemstones, settings, and finished jewelry do not qualify for upgrading.

              </p>
            </li>
            <li>
              <p>The original diamond value may not exceed $15,000.</p>
            </li>
            <li>
              <p>
                You will receive a credit equal to 100% of the original price of the diamond toward
                the purchase of a new diamond.

              </p>
            </li>
            <li>
              <p>
                A 20% restocking fee of the original diamond value will be applied.

              </p>
            </li>
            <li>
              <p>
                The price of your new diamond must be at least twice the price of your original
                diamond.
              </p>
            </li>
            <li>
              <p>
                Your diamond must be in its original condition with the original certificate report
              </p>
            </li>
            <li>
              <p>
                The original certificate must be returned in reusable condition. Fees may apply for
                any damaged or unusable certificate.

              </p>
            </li>
            <li>
              <p>
                Any damage to the original diamond may disqualify the diamond from our upgrade
                program. In rare instances, a diamond may have incurred damage through daily
                wear. In this case Yadav will still accept the diamond, but the cost of polishing and
                reshaping the diamond will be deducted from the original price.

              </p>
            </li>
            <li>
              <p>
                If your diamond is set in a Yadav setting and is in reusable condition, we can use it
                to set your new diamond for free. If the setting is not in reusable condition, an
                additional fee will apply for the modification.
              </p>
            </li>
            <li>
              <p>
                An additional fee will apply to set the setting with the new diamond if it wasn’t
                originally set up with Yadav. The fee will be determined on a case-by-case basis
              </p>
            </li>
          </ul>
          <p className="mt-6 mb-2">
            If you any questions about Yadav Lifetime Upgrade, please call us at{' '}
            <a
              href="tel:1-888-968-8810"
              className="text-accent-9"
              title="Telephone"
            >
              {' '}
              1-888-968-8810{' '}
            </a>
          </p>
        </section>
        <section className="mb-6">
          <h2 className="font-heading font-normal text-xl mb-2">Pricing</h2>
          <p className="mb-2">
            YADAV is committed to providing current and accurate information on our website,
            email, chat, and telephone and in store communications.
            We do not, however, warrant that this information is always correct, accurate or complete.
            In rare circumstances, data, including prices, may be inaccurately displayed on our Site
            due to system and typographical errors which may occur.
            Inadvertent errors or inaccurately advertised prices are not binding on YADAV, and may be
            adjusted by YADAV at any time.
            YADAV reserves the right to correct any and all errors when they do occur.
            If an order is placed with a lower product's listed price than the actual price, YADAV will,
            at its discretion, either advise the customer before shipping the product or cancel the order
            and notify the customer of such cancellation.
            YADAV apologizes for any inconvenience that this may cause.
          </p>
          <p className="mb-2">
            Information that we communicate is not a binding contract, and should not be treated as such.
            The sole remedy of a customer acting on incorrect information is to request a cancellation
            of the order placed as a result of this incorrect information.
            If an order has been shipped, the customer agrees to either return the product for full credit
            or pay the difference between the actual and charged prices.
            <Link href="/returns">
              <a className="text-accent-9" title='Return Policies'>
                Please review our Return Policies.
              </a>
            </Link>
          </p>
          <p className="mb-2">
            YADAV product details and prices are also subject to change without notice.
          </p>
        </section>
      </Container>
    </>
  )
}

Guarantee.Layout = Layout
